import { ChangeEvent, useCallback, useEffect, useState } from 'react'
import { useRef } from 'react'

import { ArrowRightIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'

import { isSNSDomain } from '@src/chains/solana/utils/sns'
import CopyToClipboard from '@src/components/CopyToClipboard'
import { ChainId } from '@src/configs/chainInfo'
import { Address } from '@src/types/token'
import { isPotentialENSName } from '@src/utils/ensUtils'

interface IDestinationWalletInputProps {
    isVisible: boolean
    destinationAddress: Address | undefined
    isValidDestination: boolean
    chainName: string
    chainId: number
    isResolvingSNS?: boolean
    resolvedSNSAddress?: string | null
    isResolvingENS?: boolean
    resolvedENSAddress?: string | null

    onChangeAddress: (e: ChangeEvent<HTMLInputElement>) => void
}

// Helper function to truncate addresses for display
const truncateAddress = (address: string, chars = 4): string => {
    if (!address) return ''
    const start = address.substring(0, chars)
    const end = address.substring(address.length - chars)
    return `${start}...${end}`
}

export function DestinationWalletInput(props: IDestinationWalletInputProps) {
    const {
        isVisible,
        destinationAddress,
        isValidDestination,
        chainName,
        chainId,
        isResolvingSNS = false,
        resolvedSNSAddress,
        isResolvingENS = false,
        resolvedENSAddress,
        onChangeAddress,
    } = props

    // State to track the original SNS/ENS domain for display purposes
    const [originalSNSDomain, setOriginalSNSDomain] = useState<string | null>(null)
    const [originalENSDomain, setOriginalENSDomain] = useState<string | null>(null)

    // State to hold the display value (which may be different from the actual destination address)
    const [displayValue, setDisplayValue] = useState<string>((destinationAddress as string) || '')

    // Flag to track if the user is actively editing the input
    const [isUserEditing, setIsUserEditing] = useState<boolean>(false)

    // State to track if we should show resolution errors (with debounce)
    const [showResolutionErrors, setShowResolutionErrors] = useState<boolean>(false)

    // Reference to the input element for auto-focus
    const inputRef = useRef<HTMLInputElement>(null)

    // When a new address is entered, check if it's an SNS or ENS domain and store it
    useEffect(() => {
        // Domain detection logic

        if (chainId === ChainId.Solana && destinationAddress && isSNSDomain(destinationAddress as string)) {
            setOriginalSNSDomain(destinationAddress as string)
            setOriginalENSDomain(null) // Clear ENS if we're using SNS
        } else if (
            chainId !== ChainId.Solana &&
            chainId !== ChainId.Tron &&
            destinationAddress &&
            isPotentialENSName(destinationAddress as string)
        ) {
            setOriginalENSDomain(destinationAddress as string)
            setOriginalSNSDomain(null) // Clear SNS if we're using ENS
        } else {
            // No domain detected or chain mismatch, check if we need to clear domains
            // Clear domains if they don't match anymore
            if (originalSNSDomain && (!destinationAddress || !isSNSDomain(originalSNSDomain))) {
                // Clearing SNS domain
                setOriginalSNSDomain(null)
            }
            if (originalENSDomain && (!destinationAddress || !isPotentialENSName(originalENSDomain))) {
                // Clearing ENS domain
                setOriginalENSDomain(null)
            }
        }
    }, [destinationAddress, chainId, originalSNSDomain, originalENSDomain])

    useEffect(() => {
        // Update display value based on domains

        if (originalENSDomain) {
            // Set display value to ENS domain
            setDisplayValue(originalENSDomain)
        } else if (originalSNSDomain) {
            // Set display value to SNS domain
            setDisplayValue(originalSNSDomain)
        } else if (destinationAddress) {
            // Set display value to destination address
            setDisplayValue(destinationAddress as string)
        }
    }, [originalENSDomain, originalSNSDomain, destinationAddress])

    // Create a wrapper for the onChangeAddress function to handle input changes directly
    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value
        // Handle input change

        // Set the editing flag to prevent the useEffect from overriding user edits
        setIsUserEditing(true)

        // Reset the resolution error flag when user is typing
        setShowResolutionErrors(false)

        // Update the display value immediately for a responsive UI
        setDisplayValue(newValue)

        // If we're editing a resolved address, immediately clear the original domains
        // This ensures the ENS/SNS display is removed right away
        if (originalENSDomain || originalSNSDomain) {
            // Editing resolved address, clear original domains
            setOriginalENSDomain(null)
            setOriginalSNSDomain(null)

            // Focus the input field and position cursor at the end
            setTimeout(() => {
                if (inputRef.current) {
                    inputRef.current.focus()
                    // Position cursor at the end of the input
                    const length = inputRef.current.value.length
                    inputRef.current.setSelectionRange(length, length)
                }
            }, 0)
        }

        // Call the original onChangeAddress function
        onChangeAddress(e)

        // Keep the editing flag on for a longer period to prevent any useEffect updates
        setTimeout(() => {
            setIsUserEditing(false)
        }, 1000)
    }

    // Update display value when destination address changes or ENS resolution completes
    // But only when not actively editing (to prevent overriding user edits)
    useEffect(() => {
        // Update display value when not editing

        // Skip updates if the user is actively editing
        if (isUserEditing) {
            // User is editing, skip display value update
            return
        }

        // If there's no destination address, clear the display value
        if (!destinationAddress) {
            // No destination address, clear display value
            setDisplayValue('')
            return
        }

        // When an ENS name is resolved, show the resolved address instead
        if (originalENSDomain && resolvedENSAddress && !isResolvingENS) {
            // Set display value to resolved ENS address
            setDisplayValue(resolvedENSAddress)
            return
        }

        // When an SNS name is resolved, show the resolved address instead
        if (originalSNSDomain && resolvedSNSAddress && !isResolvingSNS) {
            // Set display value to resolved SNS address
            setDisplayValue(resolvedSNSAddress)
            return
        }
    }, [
        destinationAddress,
        isResolvingENS,
        resolvedENSAddress,
        isResolvingSNS,
        resolvedSNSAddress,
        originalENSDomain,
        originalSNSDomain,
        isUserEditing,
    ])

    // Debounce the resolution error display
    useEffect(() => {
        let timer: NodeJS.Timeout | null = null

        // If we have a domain that might need resolution
        if ((originalENSDomain || originalSNSDomain) && !isUserEditing) {
            // Only start the timer if resolution has completed
            if (!isResolvingENS && !isResolvingSNS) {
                // Set a timer to show errors after a delay
                timer = setTimeout(() => {
                    setShowResolutionErrors(true)
                }, 1500) // 1.5 seconds delay before showing error
            }
        } else {
            // If no domain or user is editing, don't show errors
            setShowResolutionErrors(false)
        }

        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [originalENSDomain, originalSNSDomain, isResolvingENS, isResolvingSNS, isUserEditing])

    // handler to read from clipboard and forward as a ChangeEvent
    const handlePasteClick = useCallback(async () => {
        try {
            const text = await navigator.clipboard.readText()
            // create a fake event object
            const fakeEvent = {
                target: { value: text },
            } as ChangeEvent<HTMLInputElement>
            onChangeAddress(fakeEvent)
        } catch (err) {
            console.error('Failed to read clipboard contents: ', err)
        }
    }, [onChangeAddress])

    if (!isVisible) {
        return null
    }

    // Check if the address is an SNS domain, we're on Solana chain, AND the current input matches the resolved address
    // Also check that the user is not currently editing
    const isSNS =
        chainId === ChainId.Solana &&
        destinationAddress &&
        originalSNSDomain &&
        resolvedSNSAddress &&
        displayValue === resolvedSNSAddress &&
        !isUserEditing

    // Check if the address is an ENS domain, we're on an EVM chain, AND the current input matches the resolved address
    // This ensures we only show the ENS UI after successful resolution AND while the resolved address is unchanged
    // Also check that the user is not currently editing
    const isENS =
        chainId !== ChainId.Solana &&
        chainId !== ChainId.Tron &&
        destinationAddress &&
        originalENSDomain &&
        resolvedENSAddress &&
        displayValue === resolvedENSAddress &&
        !isUserEditing

    const receiverWalletPlaceholder =
        chainId === ChainId.Solana
            ? `Add receiver wallet or SNS domain (.sol) on ${chainName}`
            : chainId === ChainId.Tron || chainId === ChainId.Bitcoin
              ? `Add receiver wallet on ${chainName}`
              : `Add receiver wallet or ENS domain (.eth) on ${chainName}`

    return (
        <div className='relative mt-5'>
            <div className='relative'>
                <div className='relative w-full'>
                    {/* Create a custom input with badge and arrow - only show when the current input exactly matches the resolved address */}
                    {isSNS || isENS ? (
                        <div className='relative mb-0 flex w-full items-center overflow-x-auto rounded-xl border border-[#2A2C3180] bg-transparent px-3 py-3'>
                            <div className='flex flex-shrink-0 items-center'>
                                <span className='inline-flex items-center rounded-full bg-green-500/10 px-2 py-0.5 font-mono text-xs text-green-500 shadow-sm'>
                                    {originalSNSDomain || originalENSDomain}
                                </span>
                            </div>
                            <ArrowRightIcon className='mx-2 h-3 w-3 flex-shrink-0 text-gray-400' />
                            <div className='flex flex-grow items-center pr-12'>
                                {/* Always show the input field to maintain editability */}
                                <input
                                    ref={inputRef}
                                    className='w-full bg-transparent py-0.5 text-xs leading-normal outline-none'
                                    value={displayValue}
                                    onChange={handleInputChange}
                                    placeholder={`Address on ${chainName}`}
                                    spellCheck={false}
                                    autoFocus
                                />
                            </div>
                            {!!destinationAddress && (
                                <div className='absolute right-3 top-1/2 flex -translate-y-1/2 items-center'>
                                    {(isSNS && resolvedSNSAddress) || (isENS && resolvedENSAddress) ? (
                                        <div className='mr-2 flex items-center'>
                                            <CheckCircleIcon className='h-4 w-4 text-green-500' />
                                        </div>
                                    ) : null}
                                    <CopyToClipboard
                                        textToCopy={
                                            isSNS && resolvedSNSAddress
                                                ? resolvedSNSAddress
                                                : isENS && resolvedENSAddress
                                                  ? resolvedENSAddress
                                                  : destinationAddress
                                        }
                                        anchorId='dest-address-3'
                                    />
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className='destination-input-container flex items-center rounded-md bg-mach-darkBlue p-1.5 pl-3'>
                            <input
                                className='destination-input flex-1 bg-inherit text-sm'
                                value={displayValue}
                                onChange={handleInputChange}
                                placeholder={receiverWalletPlaceholder}
                                spellCheck={false}
                            />
                            {!!destinationAddress ? (
                                <div className='h-7 w-7'>
                                    <CopyToClipboard textToCopy={destinationAddress} anchorId='dest-address-3' />
                                </div>
                            ) : (
                                <button
                                    type='button'
                                    onClick={handlePasteClick}
                                    className='destination-paste-button rounded-md border-mach-button-tabs-border bg-mach-gray px-1.5 py-1.5 text-[12px] leading-[16px] text-white'
                                >
                                    Paste
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Show loading indicator while resolving */}
                {(isResolvingSNS || isResolvingENS) && (
                    <div className='mt-1 text-xs text-gray-400'>Resolving domain...</div>
                )}

                {/* Show error when ENS resolution failed - with debounce */}
                {showResolutionErrors && !isResolvingENS && originalENSDomain && !resolvedENSAddress && (
                    <div className='mt-1 text-xs text-rose-600'>Could not resolve ENS domain</div>
                )}

                {/* Show error when SNS resolution failed - with debounce */}
                {showResolutionErrors && !isResolvingSNS && originalSNSDomain && !resolvedSNSAddress && (
                    <div className='mt-1 text-xs text-rose-600'>Could not resolve SNS domain</div>
                )}
            </div>

            {/* Error message for invalid address */}
            {!isValidDestination && destinationAddress && destinationAddress.length > 0 && !isSNS && !isENS && (
                <span className='text-xs text-rose-600'>Not a valid {chainName} wallet address</span>
            )}
        </div>
    )
}
